// src/Components/ImportMarkers/ExcelMenu.js

import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Checkbox from '@mui/material/Checkbox';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';

const DARK_BLUE = '#00008B';
const cellStyle = { paddingLeft: '8px', paddingRight: '8px' };

export default function ExcelMenu({
  localMenus,
  selectedMenuId,
  handleSelectMenu,
  handleMenuTitleChange,
  isLoadingMenus,
  makeUppercase,
  handleCopyMenu,
  handlePasteMenu,
  copiedMenuTemplate,
}) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        p: 0,
        position: 'relative',
      }}>
      {/* Floating Action Box perfectly aligned with the Top Toolbar's Cancel block */}
      {!isLoadingMenus && (
        <Box
          sx={{
            position: 'absolute',
            top: '1px', // Immediately below the 1px border of the TableContainer
            right: '5px', // Exact match to the Top Toolbar paddingRight
            width: 'calc((100% - 10px) / 6)', // Exact width of the 6th flex:1 block from the top toolbar
            height: '55px', // Fixed height to match the header row
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 100, // Above the TableHead contents
          }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Tooltip
              title='Copy Selected Template'
              placement='top'
              arrow
              slotProps={{ popper: { sx: { zIndex: 100020 } } }}>
              <span>
                <IconButton
                  size='small'
                  onClick={handleCopyMenu}
                  disabled={!selectedMenuId}
                  sx={{ p: 0.25 }}>
                  <ContentCopyIcon
                    sx={{ fontSize: '24px', color: selectedMenuId ? '#2e7d32' : 'grey' }}
                  />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip
              title='Paste Template'
              placement='top'
              arrow
              slotProps={{ popper: { sx: { zIndex: 100020 } } }}>
              <span>
                <IconButton
                  size='small'
                  onClick={handlePasteMenu}
                  disabled={!copiedMenuTemplate}
                  sx={{ p: 0.25 }}>
                  <ContentPasteIcon
                    sx={{
                      fontSize: '24px',
                      color: copiedMenuTemplate ? '#2e7d32' : 'grey',
                    }}
                  />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </Box>
      )}

      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          flexGrow: 1,
          width: '100%',
          boxSizing: 'border-box',
          borderTop: '1px solid #ccc',
          borderBottom: '1px solid #ccc',
          borderLeft: 'none',
          borderRight: 'none',
          borderRadius: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
        }}>
        {isLoadingMenus ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              p: 3,
            }}>
            <CircularProgress sx={{ color: '#00B0FF' }} />
          </Box>
        ) : (
          <Table stickyHeader size='small' sx={{ width: '100%' }}>
            <TableHead>
              <TableRow
                sx={{
                  height: '55px',
                  '& th': {
                    backgroundColor: '#e8f5e9',
                    fontWeight: 'bold',
                    borderBottom: '1px solid #ccc',
                    py: '2px',
                    ...cellStyle,
                  },
                }}>
                <TableCell
                  padding='checkbox'
                  sx={{ width: '64px', textAlign: 'center' }}></TableCell>
                <TableCell align='left'>Template Name</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {localMenus.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={2} align='center' sx={{ py: 3 }}>
                    <Typography
                      color='text.secondary'
                      variant='body2'
                      sx={{ fontStyle: 'italic' }}>
                      No saved templates found. Click the Add New icon in the top toolbar
                      to create one.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                localMenus.map((row) => {
                  const isSelected = selectedMenuId === row.id;

                  return (
                    <TableRow
                      key={row.id}
                      hover
                      selected={isSelected}
                      onClick={() => handleSelectMenu(isSelected ? null : row.id)}
                      sx={{
                        height: '34px',
                        cursor: 'pointer',
                        '& td': { py: '4px', ...cellStyle },
                        '&:last-child td, &:last-child th': { border: 0 },
                        '&.Mui-selected': {
                          backgroundColor: 'transparent !important',
                        },
                        '&.Mui-selected:hover': {
                          backgroundColor: 'rgba(0, 0, 0, 0.04) !important',
                        },
                      }}>
                      <TableCell padding='checkbox' sx={{ textAlign: 'center' }}>
                        <Checkbox
                          checked={isSelected}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleSelectMenu(e.target.checked ? row.id : null);
                          }}
                          size='small'
                          sx={{
                            p: 0.5,
                            '&.Mui-checked': { color: '#1976d2' },
                          }}
                        />
                      </TableCell>
                      <TableCell align='left' onClick={(e) => e.stopPropagation()}>
                        <TextField
                          variant='outlined'
                          size='small'
                          fullWidth
                          placeholder='Enter Template Name...'
                          value={
                            makeUppercase && typeof row.title === 'string'
                              ? row.title.toUpperCase()
                              : row.title || ''
                          }
                          onFocus={() => {
                            if (!isSelected) handleSelectMenu(row.id);
                          }}
                          onChange={(e) => {
                            const val = makeUppercase
                              ? e.target.value.toUpperCase()
                              : e.target.value;
                            handleMenuTitleChange(row.id, val);
                          }}
                          sx={{
                            backgroundColor: '#e3f2fd',
                            borderRadius: '4px',
                            '& .MuiOutlinedInput-root': {
                              height: '26px',
                              '& fieldset': {
                                borderColor: '#90caf9',
                              },
                              '&:hover fieldset': {
                                borderColor: '#1976d2',
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: '#1976d2',
                              },
                            },
                            '& .MuiInputBase-input': {
                              color: DARK_BLUE,
                              fontSize: '0.875rem',
                              fontWeight: 'normal',
                              py: '0px',
                              px: '8px',
                              textTransform: makeUppercase ? 'uppercase' : 'none',
                            },
                          }}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        )}
      </TableContainer>
    </Box>
  );
}
